<template>
  <PanelGrid
    v-if="profile"
    class="ps-published-workspace"
    :gridType="publishedPanelMode"
    :isFluidLayout="isFluidLayout"
  >
    <template #left>
      <ItemCodesPanel
        v-if="selectedItem"
        :item="selectedItem"
      />
      <div
        v-else
        class="preview-fallback"
      >
        Новость не выбрана
      </div>
    </template>
    <template #main>
      <PublishedPanel
        ref="publishedPanel"
        :countUnreadNews="countUnreadNews"
        :dates="dates"
        :errors="errors"
        :filters="filters"
        :hasMoreItems="publishedStore.hasMoreItems"
        :isFetching="publishedStore.isFetching"
        :items="items"
        :mode="publishedPanelMode"
        :selectedItemId="selectedId"
        :preset="preset"
        :searchMode="searchMode"
        :showSearch="showSearch"
        :isReversed="publishedStore.isReversed"
        @clear="clearFilter"
        @clickUnread="showUnread"
        @onOpenActionPopover="() => publishedStore.toggleLockHead(true)"
        @reachedStart="onReachedStart"
        @reachedEnd="onReachedEnd"
        @repeat="publishedStore.reload()"
        @resetFilters="resetFilters"
        @savePreset="savePreset"
        @select="selectItem"
        @toggleTree="treePanelOpened = !treePanelOpened"
        @updateFilters="updateFilters"
        @applyPreset="applyPreset"
        @clearPreset="clearPreset"
        @search="search"
        @clearSearch="clearSearch"
        @toggleReverse="toggleReverse"
        @update:showSearch="updateShowSearch"
      />
      <NoSoundAlert />
    </template>
    <template #preview>
      <PreviewPanel
        v-if="selectedItem"
        :item="selectedItem"
        :mode="publishedPanelMode"
        :searchMode="searchMode"
      />
      <div
        v-else
        class="preview-fallback"
      >
        Новость не выбрана
      </div>
    </template>
  </PanelGrid>
</template>

<script setup lang="ts">
import { refThrottled, useThrottleFn } from '@vueuse/core'
import { ref, computed, watch, useTemplateRef } from 'vue'
import ItemCodesPanel from './ItemCodesPanel.vue'
import PreviewPanel from './preview/PreviewPanel.vue'
import PublishedPanel from './PublishedPanel.vue'
import PanelGrid from '@/ContextTab/layout/grid/PanelGrid.vue'
import { endOfToday, startOfToday, toISOStringWithTimezone } from '@/utils/dates'
import { useProfile } from '@/ContextTab/modules/user/composables/useProfile'
import { base64ToUtf, utfToBase64 } from '@/utils/base64'
import { useScreenBreakpoints } from '@/ContextTab/composables/useScreenBreakpoints'
import { usePublishedNewsStore } from '@/ContextTab/stores/newsSet/published/published'
import { usePublishedPresetsStore } from '@/ContextTab/stores/newsSet/published/presets'
import { usePublishedFiltersStore } from '@/ContextTab/stores/newsSet/published/filters'
import NoSoundAlert from '@/ContextTab/modules/news/published/components/sound/noSoundAlert.vue'
import type { NewsItemPublished, PublishedFilterConfiguratorType } from './types'
import type { NewsSetFilters } from '@/lib/types'


const publishedStore = usePublishedNewsStore()
const presetStore = usePublishedPresetsStore()
const filterStore = usePublishedFiltersStore()

if (window.location.hash) {
  try {
    const base64 = window.location.hash.slice(1, window.location.hash.length)
    const decoded = base64ToUtf(base64)
    const data = JSON.parse(decoded)
    if (data) {
      await presetStore.fetchPresets()
      await applyPreset(data.id)
    }
  } catch (err) {
    console.warn('Не удалось раскодировать пресет из адресной строки', err)
  }
}

publishedStore.loadNext()

const { isScreenSmall } = useScreenBreakpoints()
const isFluidLayout = computed(() => !isScreenSmall.value)

const publishedPanel = useTemplateRef<InstanceType<typeof PublishedPanel>>('publishedPanel')

const items = computed(() => publishedStore.newsList)
const countUnreadNews = computed(() => publishedStore.lockedHeadSet.length)

const { publishedPanelMode, profile } = useProfile({})

const filters = computed(() => filterStore.filters)

const selectedId = ref(1)
const selectedIdThrottled = refThrottled(selectedId, 200)

const rightSupplyShowed = ref(false)
const treePanelOpened = ref(true)

const selectedItem = computed(() =>
  items.value?.find((item: any) => item?.id === selectedIdThrottled.value),
)

const selectItem = (item: NewsItemPublished) => {
  if (item.id != null) {
    selectedId.value = item.id
  }
  rightSupplyShowed.value = true

  if (item.id === items.value?.[0]?.id && !publishedStore.isReversed) {
    if (countUnreadNews.value === 0) {
      publishedStore.toggleLockHead(false)
    }
  } else {
    publishedStore.toggleLockHead(true)
  }
}

const errors = computed(() => publishedStore.error)

const defaultDates = ref({
  start: toISOStringWithTimezone(startOfToday()),
  end: toISOStringWithTimezone(endOfToday()),
})

const dates = ref({
  start: defaultDates.value.start,
  end: defaultDates.value.end,
})

watch(items, (next, prev) => {
  if (next[0] && next[0].id !== prev?.[0]?.id) {
    if (!publishedStore.isLockedHead) {
      selectItem(next[0])
    }
  }
})

const onReachedStart = (isVisible: boolean) => {
  publishedStore.toggleLockHead(!isVisible)
  if (isVisible) {
    publishedStore.slice()
  }
}

const onReachedEnd = useThrottleFn(() => {
  publishedStore.loadNext()
}, 500, false, true)

function showUnread() {
  if (!publishedStore.isReversed) {
    if (publishedPanel.value?.getScrollTopSize()) {
      publishedPanel.value?.scrollToTop()
    } else {
      publishedStore.toggleLockHead(false)
    }
  } else {
    toggleReverse(false)
  }
}

const toggleReverse = (value: boolean) => {
  publishedStore.toggleLockHead(value)
  publishedStore.setReversed(value)
}

const preset = computed(() =>
  presetStore.presets?.find((preset) => preset?.id === presetStore.currentPresetId),
)

const savePreset = async (
  filters: Partial<NewsSetFilters>,
  name: string | null = null,
) => {
  if (preset.value?.id) {
    await presetStore.updatePreset(preset.value.id, {
      ...filters,
      name: name || undefined,
    })
  }
}

async function updateFilters(filters: Partial<NewsSetFilters>) {
  await filterStore.updateFilters(filters)
}

async function resetFilters() {
  await filterStore.resetFilters()
  window.location.hash = ''
}

async function clearFilter(name: PublishedFilterConfiguratorType) {
  await filterStore.clearFilter(name)
}

async function applyPreset(id: number) {
  const applyResult = presetStore.applyPreset(id)
  if ('id' in applyResult) {
    window.location.hash = utfToBase64(
      JSON.stringify({
        id: applyResult.id,
        name: applyResult.name,
      }),
    )
    await updateFilters(applyResult)
  }
}

function clearPreset() {
  presetStore.clearPreset()
  window.location.hash = ''
  resetFilters()
}

const searchMode = computed(() => publishedStore.searchMode ?? false)
const showSearch = computed(() => publishedStore.showSearch ?? false)
const updateShowSearch = (show: boolean) => publishedStore.updateShowSearch(show)

async function search() {
  if (!searchMode.value) {
    await publishedStore.switchMode()
  } else {
    await publishedStore.search()
  }
}

function clearSearch() {
  if (searchMode.value) {
    publishedStore.switchMode()
    publishedPanel.value?.scrollToTop()
  }
}
</script>

<style scoped>
.preview-fallback {
  background-color: #f2f2f2;
  color: #777;
  opacity: 0.8;

  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.alert {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 100;
  transform: translate(-50%, -50%);
  text-align: center;
  font-size: 16px;
  line-height: 1.5;
}
</style>
